import { getSubdomainFranchise } from '../utils/get-subdomain';
import { httpInterceptor } from './http.interceptor';

class UserService {
  private subdomain: string;

  constructor() {
    this.subdomain = getSubdomainFranchise();
  }

  /**
   * Verifica se existe um pré-cadastro para o CPF informado
   * @param cpf - CPF do usuário
   * @returns Promise com os dados do pré-cadastro
   */
  async checkPreRegister(cpf: string): Promise<any> {
    try {
      const cleanCpf = cpf.replace(/\D/g, ''); // Remove caracteres não numéricos
      
      const response = await httpInterceptor.get(`/user/has-pre-register/${cleanCpf}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': btoa(this.subdomain),
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Erro ao verificar pré-cadastro');
      }

      return data;
    } catch (error) {
      console.error('Error checking pre-register:', error);
      throw error;
    }
  }

  /**
   * Atualiza cadastro incompleto do usuário (endereço e PIX)
   * @param data - Dados para atualizar
   * @returns Promise com resultado da operação
   */
  async atualizarCadastroIncompleto(data: any): Promise<any> {
    try {
      const response = await httpInterceptor.patch('/user/incomplete', data, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': btoa(this.subdomain),
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Erro ao atualizar cadastro');
      }

      return result;
    } catch (error) {
      console.error('Error updating incomplete registration:', error);
      throw error;
    }
  }

  /**
   * Busca informações de CEP
   * @param cep - CEP para consulta
   * @returns Promise com dados do endereço
   */
  async searchCep(cep: string): Promise<any> {
    try {
      const cleanCep = cep.replace(/\D/g, '');
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      
      if (!response.ok) {
        throw new Error('Erro ao buscar CEP');
      }

      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar CEP:', erro);
      throw erro;
    }
  }

  // Get codigo de indicação do usuário logado
  async getUserIndicationCode(userId: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/user/indication-code/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': btoa(this.subdomain),
        }
      });
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar código de indicação');
      }
      return data;
    } catch (error) {
      console.error('Error fetching user indication code:', error);
      throw error;
    }
  }

  /**
   * Atualiza o Instagram do usuário
   * @param instagram - Username do Instagram
   * @returns Promise com resultado da operação
   */
  async updateInstagram(instagram: string, userId: number): Promise<any> {
    try {
      const response = await httpInterceptor.patch('/user/update-instagram', 
        { instagram, userId },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': btoa(this.subdomain),
          }
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Erro ao atualizar Instagram');
      }

      return result;
    } catch (error) {
      console.error('Error updating Instagram:', error);
      throw error;
    }
  }

  /**
   * Busca todas as coordenadoras do sistema
   * @returns Promise com lista de coordenadoras
   */
  async getCoordenadoras(): Promise<any[]> {
    try {
      const response = await httpInterceptor.get('/user/coordenadoras', {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': btoa(this.subdomain),
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar coordenadoras');
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching coordenadoras:', error);
      throw error;
    }
  }

  async getColaboradores(): Promise<any[]> {
    try {
      const response = await httpInterceptor.get('/user/colaboradores', {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': btoa(this.subdomain),
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar colaboradores');
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching colaboradores:', error);
      throw error;
    }
  }

  /**
   * Realiza primeiro acesso do usuário
   * @param data - Dados do formulário de registro
   * @param franchise - Franquia selecionada
   * @param indicadorCodigo - Código de indicação (opcional)
   * @returns Promise com resultado da operação
   */
  async firstAccess(data: any, franchise: string = '', indicadorCodigo?: string): Promise<any> {
    try {
      const url = '/user/first-access';
      if (!franchise) {
        franchise = this.subdomain;
      }

      // Adicionar código de indicação se fornecido
      const payload = indicadorCodigo ? { ...data, indicador_codigo: indicadorCodigo } : data;

      const response = await httpInterceptor.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': btoa(franchise),
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Erro ao realizar primeiro acesso');
      }

      return result;
    } catch (error: any) {
      console.error('Error during first access:', error);
      throw error;
    }
  }

  /**
   * Busca todos os usuários do sistema com paginação e filtros
   * @param page - Número da página (começa em 1)
   * @param size - Quantidade de registros por página
   * @param search - Filtro de busca (nome, email, CPF/CNPJ, código)
   * @param sort - Campo para ordenação
   * @param order - Ordem: ASC ou DESC
   * @param status - Filtro de status: 'ativo' ou 'inativo' (opcional)
   * @returns Promise com dados paginados e informações de paginação
   */
  async getAllUsers(
    page: number = 1,
    size: number = 50,
    search: string = '',
    sort: string = 'nome_razao_social',
    order: string = 'ASC',
    status: string = '',
    coordenadoraId: number = 0,
    role: string = '',
  ): Promise<any> {
    try {
      // Construir query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());
      params.append('sort', sort);
      params.append('order', order);
      if (search.trim()) {
        params.append('search', search.trim());
      }
      if (status === 'ativo') {
        params.append('ativo', 'true');
      } else if (status === 'inativo') {
        params.append('inativo', 'true');
      }
      if (coordenadoraId && coordenadoraId > 0) {
        params.append('coordenadoraId', coordenadoraId.toString());
      }
      if (role && role.trim()) {
        params.append('role', role.trim());
      }

      const response = await httpInterceptor.get(`/user/all?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': btoa(this.subdomain),
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Erro ao buscar usuários');
      }

      return result;
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  }

  /**
   * Atribui uma coordenadora a um usuário
   * @param coordenadoraId - ID da coordenadora
   * @param userId - ID do usuário
   * @returns Promise com resultado da operação
   */
  async atribuirCoordenadora(coordenadoraId: number, userId: number): Promise<any> {
    try {
      const response = await httpInterceptor.post('/user/atribui-coord', 
        { coordenadoraId, userId },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': btoa(this.subdomain),
          }
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Erro ao atribuir coordenadora');
      }

      return result;
    } catch (error) {
      console.error('Error assigning coordenadora:', error);
      throw error;
    }
  }

  /**
   * Reseta a senha de um usuário
   * @param userId - ID do usuário
   * @returns Promise com resultado da operação
   */
  async resetUserPassword(userId: number): Promise<any> {
    try {
      const response = await httpInterceptor.post('/user/reset-password', 
        { userId },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': btoa(this.subdomain),
          }
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Erro ao resetar senha');
      }

      return result;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }

  /**
   * Busca detalhes de um usuário pelo ID
   * @param userId - ID do usuário
   * @returns Promise com dados do usuário
   */
  async getUserDataById(userId: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/user/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': btoa(this.subdomain),
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar dados do usuário');
      }

      return data;
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw error;
    }
  }

  /**
   * Busca revendedoras coordenadas por uma coordenadora
   * @param coordinadorId - ID da coordenadora
   * @returns Promise com lista de revendedoras
   */
  async getRevendedorasCoordenadas(coordinadorId?: number): Promise<any[]> {
    try {
      let url = '/user/revendedoras-coordenadas';
      if (coordinadorId) {
        url += `/${coordinadorId}`;
      }

      const response = await httpInterceptor.get(url, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': btoa(this.subdomain),
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar revendedoras');
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching revendedoras coordenadas:', error);
      throw error;
    }
  }

  /**
   * Atualiza dados de um usuário
   * @param userData - Dados do usuário para atualizar
   * @returns Promise com resultado da operação
   */
  async updateUser(userData: any): Promise<any> {
    try {
      const response = await httpInterceptor.patch('/user', userData, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': btoa(this.subdomain),
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Erro ao atualizar user');
      }

      return result;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  /**
   * Atualiza a chave PIX de um usuário
   * @param userId - ID do usuário
   * @param pix - Nova chave PIX
   * @returns Promise com resultado da operação
   */
  async updatePix(userId: number, pix: string): Promise<any> {
    try {
      const response = await httpInterceptor.patch('/user/update-pix', {
        userId: userId,
        pix: pix
      }, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': btoa(this.subdomain),
        }
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Erro ao atualizar PIX');
      }

      return result;
    } catch (error) {
      console.error('Error updating PIX:', error);
      throw error;
    }
  }
}

export const userService = new UserService();